import { createContext, type ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@/shared/api';
import { useContentLanguages } from './contentLanguages';

/**
 * «Перевести заново» поля записи (ТЗ 17.12.5, BR-INT-SVC-69, World): запуск — у страницы записи
 * (знает тип и id сущности и вызывает `POST /admin/translations/retranslate`); поля LocalizedText
 * внутри показывают кнопку, когда автоперевод включён (`/admin/config` → `content.translation`), у
 * текста есть исходный язык и перевод уже есть (`auto` / `manual`).
 */
export type RetranslateRun = (fieldPath: string) => Promise<void>;

const RetranslateContext = createContext<RetranslateRun | null>(null);

export function RetranslateProvider({
  run,
  children,
}: {
  run: RetranslateRun | null | undefined;
  children: ReactNode;
}) {
  return <RetranslateContext.Provider value={run ?? null}>{children}</RetranslateContext.Provider>;
}

/** Путь поля формы → путь контракта перевода: `theses.2` → `theses[2]`. */
export function translationFieldPath(name: string): string {
  return name.replace(/\.(\d+)(?=\.|$)/g, '[$1]');
}

const RETRANSLATABLE: ReadonlySet<string> = new Set(['auto', 'manual']);

export function RetranslateButton({
  fieldName,
  value,
}: {
  fieldName: string;
  value: LocalizedText | null;
}) {
  const { t } = useTranslation('form');
  const run = useContext(RetranslateContext);
  const { languages, autoTranslate } = useContentLanguages();
  const [pending, setPending] = useState(false);
  const status = value?.translation_status;
  if (
    !run ||
    !autoTranslate ||
    languages.length < 2 ||
    !value?.source_lang ||
    !status ||
    !RETRANSLATABLE.has(status)
  ) {
    return null;
  }
  return (
    <button
      type="button"
      disabled={pending}
      onClick={() => {
        setPending(true);
        void run(translationFieldPath(fieldName)).finally(() => setPending(false));
      }}
      className="cursor-pointer border-0 bg-transparent p-0 text-[11px] font-medium whitespace-nowrap text-wine-bordeaux hover:underline disabled:cursor-wait disabled:opacity-60"
    >
      {t('localized.retranslate')}
    </button>
  );
}
